import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { ArrowUp, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { AppHeaderBar, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { peekSkeletonComposerText, requestSkeletonComposerSubmit, saveSkeletonComposerText } from "./newSessionSkeletonState";
import { NEW_SESSION_MASCOT_SIZE_CLASS, NEW_SESSION_SCROLL_AREA_CLASS, NEW_SESSION_WELCOME_COLUMN_CLASS } from "./newSessionLayout";
import { PROMPT_PILL_ROW_CLASS, PromptPillSkeleton, LEAD_STARTER_PILL_LAYOUT_CLASS, STARTER_PILL_ROW_CLASS, StarterPromptPillSkeleton } from "./promptPills";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import { useIsTouchPrimary } from "@/hooks/use-device";
import { assetUrl } from "@/lib/assetUrl";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { splitInstanceBasePath } from "@/lib/dashboard-routes";
import { cn } from "@repo/blacksmith-ui/cn";
import { CENTERED_LOADING_ANVIL_SIZE_CLASS, CENTERED_LOADING_ANVIL_SIZE_PX } from "@/components/agent-session/centeredLoadingAnvil";
import { useAnvilHandoffSource } from "@/components/agent-session/useAnvilHandoff";
import { animationPath } from "@/components/agent-session/useAnvilTransition";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionLoadingScreen    – centered animated anvil for existing sessions.
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

const LOADING_LOOP = "ruminating";

/** Loading state for an existing session. */
export function SessionLoadingScreen() {
  const $ = _c(1);
  const spriteRef = useRef(null);
  useAnvilHandoffSource(spriteRef, LOADING_LOOP);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="status" aria-label="Loading session" className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-app-background)]"><span ref={spriteRef} aria-hidden={true} className={cn(CENTERED_LOADING_ANVIL_SIZE_CLASS, "flex items-center justify-center")}><img src={animationPath(LOADING_LOOP, "light")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="select-none object-contain [image-rendering:pixelated] dark:hidden" /><img src={animationPath(LOADING_LOOP, "dark")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="hidden select-none object-contain [image-rendering:pixelated] dark:block" /></span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into TaskStarterCards instead, so only `pr_preview` reserves
 * the PrActionCards skeleton shape (four action pills).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading. Enter follows the real
 * composer's keymap (send on desktop, newline on touch) and the send button
 * works like Enter; the send itself waits for the real composer.
 */
export function NewSessionChatSkeleton() {
  const $ = _c(42);
  const isHydrated = useIsHydrated();
  const isTouch = useIsTouchPrimary();
  let t0;
  if ($[0] !== isHydrated) {
    t0 = isHydrated && hasPrPreviewInSearch(window.location.search);
    $[0] = isHydrated;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isPrAttached = t0;
  const seededText = isHydrated ? peekSkeletonComposerText() ?? "" : "";
  const textareaRef = useRef(null);
  const mascotRef = useRef(null);
  useAnvilHandoffSource(mascotRef);
  const [typedHasText, setTypedHasText] = useState(null);
  const hasText = typedHasText ?? seededText.trim().length > 0;
  const requestSubmit = _temp;
  let t1;
  if ($[2] !== isHydrated) {
    t1 = isHydrated && !readLeftSidebarSkeletonLayout().visible && <PanelTogglePlaceholder />;
    $[2] = isHydrated;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <AppHeaderBar inset="canvas" className="bg-app-background">{t1}</AppHeaderBar>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={mascotRef} className="relative mt-1"><img src={assetUrl("anvil-hello-light.png")} alt="[code]smith" className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden`} /><img src={assetUrl("anvil-hello-dark.png")} alt="[code]smith" className={`hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block`} /></div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col items-center">{t3}<div className="flex h-[25px] items-center"><Skeleton className="h-5 w-72 max-w-full rounded" /></div></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isHydrated || $[9] !== seededText) {
    t5 = el => {
      textareaRef.current = el;
      if (!el) {
        return;
      }
      const handoff = peekSkeletonComposerText() ?? "";
      if (isHydrated && handoff !== seededText) {
        el.value = handoff;
      }
      el.setSelectionRange(el.value.length, el.value.length);
    };
    $[8] = isHydrated;
    $[9] = seededText;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== setTypedHasText) {
    t6 = e => {
      saveSkeletonComposerText(e.target.value);
      setTypedHasText(e.target.value.trim().length > 0);
    };
    $[11] = setTypedHasText;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== isTouch) {
    t7 = e_0 => {
      if (e_0.nativeEvent.isComposing || e_0.keyCode === 229) {
        return;
      }
      if (e_0.key !== "Enter" || e_0.shiftKey || isTouch) {
        return;
      }
      e_0.preventDefault();
      requestSubmit(e_0.currentTarget.value);
    };
    $[13] = isTouch;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== seededText || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <textarea ref={t5} rows={1} autoFocus={true} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Got a task or question?" defaultValue={seededText} onChange={t6} onKeyDown={t7} className="type-chat-prose pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70" />;
    $[15] = seededText;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = !hasText;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => requestSubmit(textareaRef.current?.value ?? "");
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = hasText ? "cursor-pointer border-[#2a2c2c] bg-blacksmith-yellow text-neutral-900 hover:brightness-95 dark:text-neutral-900" : "cursor-not-allowed border-black/[0.08] bg-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500";
  let t13;
  if ($[22] !== t12) {
    t13 = cn("pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border transition-all focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:outline-none focus-visible:ring-0", t12);
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} />;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t10 || $[26] !== t13) {
    t15 = <div className="absolute inset-y-0 right-0 flex items-center"><button type="button" aria-label="Send message" disabled={t10} onClick={t11} className={t13}>{t14}</button></div>;
    $[25] = t10;
    $[26] = t13;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t8) {
    t16 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t8}{t9}{t15}</div></div>;
    $[28] = t15;
    $[29] = t8;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS;
  let t18;
  if ($[31] !== isPrAttached) {
    t18 = isPrAttached ? Array.from({
      length: PR_ACTION_PILL_COUNT
    }, _temp2) : <><StarterPromptPillSkeleton className={LEAD_STARTER_PILL_LAYOUT_CLASS} /><StarterPromptPillSkeleton className="grow" /></>;
    $[31] = isPrAttached;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <div className="flex w-full flex-col gap-4"><div className={t17}>{t18}</div></div>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t16 || $[37] !== t19) {
    t20 = <div className={NEW_SESSION_SCROLL_AREA_CLASS}><div className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}>{t4}<div className="flex w-full flex-col gap-3">{t16}{t19}</div></div></div>;
    $[36] = t16;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t2 || $[40] !== t20) {
    t21 = <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t2}{t20}</div>;
    $[39] = t2;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  return t21;
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 * An enterprise instance prefix (`/enterprise/ghe/acme/sessions/new`) is
 * stripped first.
 */
function _temp2(_, i) {
  return <PromptPillSkeleton key={i} className="w-32" />;
}
function _temp(text) {
  if (text.trim()) {
    requestSkeletonComposerSubmit(text);
  }
}
function isNewSessionPath(path: string): boolean {
  const {
    pathname
  } = splitInstanceBasePath(path.split(/[?#]/)[0]);
  const segments = pathname.split("/").filter(Boolean);
  return segments[1] === "sessions" && (segments.length === 2 || segments[2] === "new");
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton() {
  const $ = _c(7);
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const isHydrated = useIsHydrated();
  const path = isHydrated ? window.location.pathname : router.asPath;
  const t0 = surface !== "extension";
  let t1;
  if ($[0] !== t0) {
    t1 = <SidebarSkeleton showSearch={t0} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== path) {
    t2 = isNewSessionPath(path) ? <NewSessionChatSkeleton /> : <SessionLoadingScreen />;
    $[2] = path;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="pointer-events-none flex h-full">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}